// @flow

import React, {useRef, useImperativeHandle, forwardRef, useMemo} from 'react';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import {DataTable as DataTableContent} from './data-table.component';
import {DataTableDragEventsContextProvider} from './contexts/data-table-drag-events-context';
import {ScrollContextProvider} from './contexts/data-table-scroll-context';
import {DataTableCombinedContextProvider} from './contexts/data-table-combined-context';
import {DataTableContext} from './contexts/data-table-context';

import type {DataTableProps, DefaultProps} from './typedef';


type Props<DataRow, DataColumn> = {
    ...DataTableProps<DataRow, DataColumn>,
    ...DefaultProps<DataColumn>,
};

export const DataTable: React$AbstractComponent<Props<Object, Object>, HTMLElement> =
    forwardRef<Props<Object, Object>, HTMLElement>((props, ref) => {
        const innerRef = useRef<HTMLElement | null>(null);
        const headerHeightRef = useRef<number>(0);
        const onRef = useCallbackWithRef(element => {
            innerRef.current = element;
        });

        useImperativeHandle(ref, () => innerRef.current, []);

        const dataTableContext = useMemo(() => ({
            dataTableRef: innerRef,
            mainHeaderHeightRef: headerHeightRef,
        }), []);

        return (
            <DataTableDragEventsContextProvider>
                <ScrollContextProvider
                    scrollRef={props.scrollRef}
                    disabledScroll={props.disabledScroll}
                    hasContent={!!(props.data?.rows?.length || props.data?.treeRows?.length)}>
                    <DataTableCombinedContextProvider>
                        <DataTableContext.Provider value={dataTableContext}>
                            <DataTableContent {...props} onRef={onRef}/>
                        </DataTableContext.Provider>
                    </DataTableCombinedContextProvider>
                </ScrollContextProvider>
            </DataTableDragEventsContextProvider>
        );
    });

DataTable.displayName = 'DataTable';

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");
